import type { Event } from '@amplitude/analytics-types';
import { init } from '@amplitude/engagement-browser';
import { useEffect, useRef } from 'react';
import { useUser } from '../../api/user/useUser';
import { isImpersonatedUser } from '@helios/frontend-auth';

const useAmplitudeEngagement = () => {
  const { user, isAuthenticated } = useUser();
  const bootedRef = useRef(false);
  const initRef = useRef(false);

  useEffect(() => {
    const amplitudeAPIKey = import.meta.env.VITE_NEXT_PUBLIC_AMPLITUDE_API_KEY;

    if (!amplitudeAPIKey || isImpersonatedUser() || initRef.current) {
      return;
    }

    init(amplitudeAPIKey);
    initRef.current = true;
  }, []);

  useEffect(() => {
    if (!initRef.current || !isAuthenticated || !user?.auth0Id || bootedRef.current || isImpersonatedUser()) {
      return;
    }

    window.engagement?.boot({
      user: {
        user_id: user.auth0Id,
        user_properties: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          locale: user.languageCode,
          source: user.source,
          propertyIds: user.propertyIds,
          ...user.segmentation,
        },
      },
      integrations: [
        {
          track: (event: Event) => {
            window.analytics?.track(event.event_type, event.event_properties);
          },
        },
      ],
    });

    bootedRef.current = true;

    const analytics = window.analytics as {
      on?: (event: string, callback: (...args: unknown[]) => void) => void;
    };
    const engagement = window.engagement as {
      forwardEvent?: (event: { event_type: string; event_properties?: Record<string, unknown> }) => void;
    };

    analytics?.on?.('track', (event: unknown, properties: unknown) => {
      engagement?.forwardEvent?.({
        event_type: event as string,
        event_properties: properties as Record<string, unknown>,
      });
    });

    analytics?.on?.('page', (category: unknown, name: unknown, properties: unknown) => {
      engagement?.forwardEvent?.({
        event_type: (name || category) as string,
        event_properties: properties as Record<string, unknown>,
      });
    });
  }, [
    isAuthenticated,
    user?.auth0Id,
    user?.email,
    user?.firstName,
    user?.lastName,
    user?.languageCode,
    user?.source,
    user?.propertyIds,
    user?.segmentation,
  ]);
};

export default useAmplitudeEngagement;
